import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react'
import { FileBgIcon } from '../agent-dashboard-order-management/assets';
import { MoreActionItem, MoreActionPopover } from '../common/more-action-popover';
import { ArrowDownToLine, EditIcon, MoreHorizontal, Star } from 'lucide-react';
import { RadioButton } from '../common/radio-button';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmModal } from '../ui/modal';
import { RenameIcon, TrashIcon } from '../ui/icon';
import { formatDateSmart } from '@/utils/date-formatter';
import { TextareaEditable } from '../common/textarea-editable';
import { useToast } from '@/hooks/use-toast';
import { IconButton } from '../common/icon-button';
import fileBg from '@/assets/assets/file-bg.webp'
import { Image } from '../common/image';
import { MoveIcon } from '../assets-material-item/assets';
import { AgentStarIcon } from './assets';
import { MarkdownPreviewModal } from '../common/markdown-preview-modal';
import { ItemDetailData } from '@/services/knowledge-get-folder-items';

interface ItemCardProps {
  id: number;
  type: string;
  name: string;
  time: string;
  content?: string;
  isSelected: boolean;
  openSelected: boolean;
  showCreation?: boolean;
  itemDetail?: ItemDetailData[];
  /** 选中回调 */
  onSelected?: (props: { id: number; checked: boolean }) => void;
  /** 删除回调 */
  onDelete?: (id: number) => void;
  /** 下载回调 */
  onDownload?: (id: number) => void;
  /** 重命名回调 */
  onRename?: (id: number, name: string) => void;
  /** 详情回调 */
  onDetail?: (id: number) => void;
  /** 应用回调 */
  onCreation?: (id: number) => void;
  /** 移动回调 */
  onMove?: (id: number) => void;
}

export const ItemCard = ({
  id,
  type,
  name,
  time,
  content,
  isSelected,
  showCreation,
  openSelected,
  itemDetail,
  onSelected,
  onDelete,
  onDownload,
  onRename,
  onDetail,
  onCreation,
  onMove,
}: ItemCardProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [openMarkdownPreview, setOpenMarkdownPreview] = useState(false);

  const [checked, setChecked] = useState(isSelected || false);
  // 添加状态来控制TextareaEditable的编辑状态
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    setChecked(isSelected || false);
  }, [isSelected]);

  const handleSelect = (value: boolean, id: number) => {
    setChecked(value);
    onSelected?.({ id, checked: value });
  }

  const handleRename = (newName: string) => {
    onRename?.(id, newName);
    // 重命名完成后关闭编辑状态
    setIsEditingName(false);
  };

  // 处理编辑状态变化
  const handleEditingChange = (editing: boolean) => {
    setIsEditingName(editing);
  };

  // 开始重命名操作
  const startRename = () => {
    setIsEditingName(true);
  };

  const handleDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (openSelected) {
      handleSelect(!checked, id);
      return;
    };
    if (type === 'md') {
      setOpenMarkdownPreview(true);
      return;
    }
    onDetail?.(id)
  }

  return (
    <div
      className={cn(
        "w-full h-full relative rounded-md select-none cursor-pointer",
        checked && "bg-[rgba(234,236,240,1)]",
      )}
      onClick={handleDetail}
    >
      <div
        className={cn(
          "group w-full h-[148px] relative rounded-[12px] flex flex-col items-center justify-center cursor-pointer transition-all hover:bg-[rgba(234,236,240,1)]",
          checked && openSelected && "bg-[#EAECF0]",
        )}
      >
        <div
          className='relative select-none'
        >
          {type !== 'md' && <FileBgIcon className='w-[68px] h-[84px]' />}
          {type === 'md' && <Image src={fileBg} className='w-[68px] h-[84px]' />}
          <div className={cn(
            'absolute top-10 -left-0.5 px-2 py-0.5 rounded-[6px] text-[15px] font-semibold text-white bg-[rgba(55,93,251,1)] tracking-wide',
            type === 'pdf' && 'bg-[rgba(223,28,65,1)]',
          )}>
            {type.toLocaleUpperCase()}
          </div>
        </div>
        {/* 选中按钮 */}
        {/* <div onClick={(e) => {
          e.stopPropagation()
          handleSelect(!checked, id);
        }}> */}
        <RadioButton
          onChange={(value) => handleSelect(value, id)}
          checked={checked}
          className={cn(
            "absolute size-6 top-2 left-2 rounded-[5px] border-[3px] border-white bg-[rgba(0,0,0,0.25)]",
            "opacity-0 group-hover:opacity-100",
            checked && "opacity-100 bg-[rgba(90,92,96,1)]",
          )}
        />
        {/* </div> */}
        {/* 更多操作 */}
        <div
          className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreActionPopover
            className="w-40"
            side="bottom"
            tiggerIcon={
              <MoreHorizontal className="size-5 text-black" />
            }
            tiggerClassName="hover:bg-white/60 bg-white/60 shadow-md"
          >
            <MoreActionItem
              icon={<RenameIcon className="w-4 h-4 text-[rgba(16,24,40,1)]" />}
              label={t("common.rename")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => startRename()}
            />
            {showCreation && (
              <MoreActionItem
                icon={<AgentStarIcon className="w-4 h-4 text-[rgba(16,24,40,1)]" />}
                label={t("assets.knowledge_item.card.creation")}
                className='text-[rgba(16,24,40,1)] font-medium'
                onClick={() => onCreation?.(id)}
              />
            )}
            <MoreActionItem
              icon={<ArrowDownToLine className="w-4 h-4 text-[rgba(16,24,40,1)]" />}
              label={t("common.download")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => onDownload?.(id)}
            />
            <MoreActionItem
              icon={<MoveIcon className="w-4 h-4 text-[rgba(16,24,40,1)]" />}
              label={t("assets.knowledge_item.card.move")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => onMove?.(id)}
            />
            <DeleteConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4 text-[rgba(16,24,40,1)]" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              onConfirm={() => onDelete?.(id)}
            />
          </MoreActionPopover>
        </div>
        {/* 创作按钮 */}
        {showCreation && (
          <div
            className="opacity-0 group-hover:opacity-100 absolute bottom-2 right-2"
            onClick={(e) => {
              e.stopPropagation()
              onCreation(id)
            }}
          >
            <IconButton
              icon={<AgentStarIcon className='size-5' />}
              className='hover:bg-gray-300'
            />
          </div>
        )}
      </div>
      <div className='w-full absolute -bottom-1 translate-y-full'>
        {/* <div className='w-full text-center line-clamp-2'>
          {name}
        </div> */}
        <TextareaEditable
          value={name}
          onChange={handleRename}
          maxRows={3}
          textareaClassName="max-h-16"
          textClassName="text-base text-[rgba(16,24,40,1)] font-medium"
          isEditing={isEditingName}
          onEditingChange={handleEditingChange}
        />
        <div className="w-full text-center mt-1 text-xs text-[#667085]">
          {formatDateSmart(time)}
        </div>
      </div>
      {openMarkdownPreview && (
        <MarkdownPreviewModal
          open={openMarkdownPreview}
          onOpenChange={(open) => {
            setOpenMarkdownPreview(open);
          }}
          title={name}
          content={itemDetail?.map((item) => item.itemContent).join('\n')}
        />
      )}
    </div>
  )
}
